import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { change as changeForm, formValueSelector } from "redux-form";
import styles from "./RiskAssessmentMatrixShared.module.css";
import jobStyles from "../../../../../JobStyle.module.css";
import EmergencyContactAddEditModal from "./EmergencyContactAddEditModal.jsx";
import EmergencyContactTypeEditModal from "./EmergencyContactTypeEditModal";

const FORM_NAME = "JobSwmsV3EmergencyPlansContainerForm";

export default function JobSwmsV3EmergencyContactsSection({
  sectionKey,
  planIndex,
  sectionData,
  loading
}) {
  const dispatch = useDispatch();
  const selector = useMemo(() => formValueSelector(FORM_NAME), []);
  const selectedTypes = useSelector((state) => selector(
    state,
    `plans[${planIndex}].sections.${sectionKey}.data.types`
  ));
  const types = Array.isArray(selectedTypes)
    ? selectedTypes
    : (
      sectionData
        && sectionData.data
        && Array.isArray(sectionData.data.types)
        ? sectionData.data.types
        : []
    );
  const change = (_field, value) => dispatch(changeForm(
    FORM_NAME,
    `plans[${planIndex}].sections.${sectionKey}.data.types`,
    value
  ));
  const [showModal, setShowModal] = useState(false);
  const [showTypeEditModal, setShowTypeEditModal] = useState(false);
  const [editContext, setEditContext] = useState(null);
  const [typeEditContext, setTypeEditContext] = useState(null);

  const typeOptions = useMemo(() => {
    return types.map((type) => ({
      label: type && type.emergency_type ? type.emergency_type : "",
      value: type && type.id ? type.id : type && type.emergency_type ? type.emergency_type : ""
    })).filter((option) => option.label);
  }, [types]);

  const onOpenAdd = () => {
    setEditContext(null);
    setShowModal(true);
  };

  const onOpenEdit = (typeId, contactId) => {
    setEditContext({ typeId, contactId });
    setShowModal(true);
  };

  const onOpenTypeEdit = (emergencyType) => {
    setTypeEditContext(emergencyType);
    setShowTypeEditModal(true);
  }

  const onDelete = (typeId, contactId) => {
    const updatedTypes = types.map((type) => {
      if (String(type.id) !== String(typeId)) {
        return type;
      }

      return {
        ...type,
        contacts: (type.contacts || []).filter((contact) => String(contact.id) !== String(contactId))
      };
    }).filter((type) => {
      // if (!type.isNew) {
      //   return true;
      // }

      return Array.isArray(type.contacts) && type.contacts.length > 0;
    });

    change(`sections.${sectionKey}.data.types`, updatedTypes);
  };

  const onCloseModal = (isSaved, payload) => {
    if (!isSaved || !payload) {
      setShowModal(false);
      return;
    }

    const emergencyType = payload.emergency_contact_type;
    const emergencyTypeName = emergencyType
      ? String(emergencyType.label || emergencyType.value || "").trim()
      : "";

    if (!emergencyTypeName) {
      setShowModal(false);
      return;
    }

    const emergencyTypeId = emergencyType && emergencyType.value && !emergencyType.__isNew__
      ? emergencyType.value
      : null;

    const updatedTypes = [...types];

    if (editContext) {
      const updatedByEdit = updatedTypes.map((type) => {
        if (String(type.id) !== String(editContext.typeId)) {
          return type;
        }

        return {
          ...type,
          contacts: (type.contacts || []).map((contact) => {
            if (String(contact.id) !== String(editContext.contactId)) {
              return contact;
            }

            return {
              ...contact,
              contact_name: payload.contact_name,
              phone_number: payload.phone_number,
              // isNew: true
            };
          })
        };
      });

      change(`sections.${sectionKey}.data.types`, updatedByEdit);
      setShowModal(false);
      setEditContext(null);
      return;
    }

    const matchedIndex = updatedTypes.findIndex((type) => {
      if (emergencyTypeId && String(type.id) === String(emergencyTypeId)) {
        return true;
      }

      return String(type.emergency_type || "").trim().toLowerCase() === emergencyTypeName.toLowerCase();
    });

    const newContact = {
      id: `contact_${Date.now()}`,
      contact_name: payload.contact_name,
      phone_number: payload.phone_number,
      // isNew: true
    };

    if (matchedIndex >= 0) {
      const targetType = updatedTypes[matchedIndex];
      updatedTypes[matchedIndex] = {
        ...targetType,
        contacts: [...(targetType.contacts || []), newContact]
      };
    } else {
      updatedTypes.push({
        id: `type_${Date.now()}`,
        emergency_type: emergencyTypeName,
        toggle: 1,
        contacts: [newContact],
        // isNew: true
      });
    }

    change(`sections.${sectionKey}.data.types`, updatedTypes);
    setShowModal(false);
    setEditContext(null);
  };

  const onTypeModalClose = (isSaved, payload) => {
    if (!isSaved || !payload) {
      setShowTypeEditModal(false);
      return;
    }

    const updatedTypes = [...types];

    if (typeEditContext) {
      const updatedByEdit = updatedTypes.map((type) => {
        if (String(type.id) !== String(typeEditContext.id)) {
          return type;
        }
        return {
          ...type,
          emergency_type: payload.emergency_type
        };
      });
      change(`sections.${sectionKey}.data.types`, updatedByEdit);
      setShowTypeEditModal(false);
      setTypeEditContext(null);
      return;
    }
  }

  const editType = editContext ? types.find((type) => String(type.id) === String(editContext.typeId)) : null;
  const editContact = editType && Array.isArray(editType.contacts)
    ? editType.contacts.find((contact) => String(contact.id) === String(editContext.contactId))
    : null;

  return (
    <div className="container-fluid my-2">
      {types.length === 0 ? (
        <div className={styles.emergencyContactsEmptyState}>
          No Emergency Contacts Available
        </div>
      ) : (
        <div className={styles.tableShell}>
          <table className={styles.likelihoodTable}>
            <thead>
              <tr>
                <th
                  className={`${styles.ramTableHeaderConsequence} ${styles.ramTableHeaderConsequenceLeft} ${styles.emergencyContactsNameHeader}`}
                >
                  Contact / Service Name
                </th>
                <th
                  className={`${styles.ramTableHeaderConsequence} ${styles.ramTableHeaderConsequenceLeft} ${styles.emergencyContactsPhoneHeader}`}
                >
                  Phone Number
                </th>
                <th
                  className={`${styles.ramTableHeaderConsequence} ${styles.ramTableHeaderConsequenceLeft} ${styles.emergencyContactsNameHeader}`}
                  width="50px">
                </th>
              </tr>
            </thead>
            <tbody>
              {types.map((type) => (
                <React.Fragment key={type.id || type.emergency_type}>
                  <tr>
                    <td colSpan={2} className={styles.emergencyContactsTypeRow}>
                      {type.emergency_type || "-"}
                    </td>
                    <td className={styles.emergencyContactsTypeRow}>
                      <span className={styles.emergencyContactActions}>
                        <button
                          type="button"
                          disabled={loading}
                          className={styles.emergencyContactActionButton}
                          onClick={() => onOpenTypeEdit(type)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className={styles.emergencyContactEditIcon}>
                            <path fill="currentColor" d="M362.7 19.3L314.3 67.7 444.3 197.7l48.4-48.4c25-25 25-65.5 0-90.5L453.3 19.3c-25-25-65.5-25-90.5 0zm-71 71L58.6 323.5c-10.4 10.4-18 23.3-22.2 37.4L1 481.2C-1.5 489.7 .8 498.8 7 505s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L421.7 220.3 291.7 90.3z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          disabled={loading}
                          className={styles.emergencyContactActionButton}
                          // onClick={() => onTypeDelete(type.id)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className={styles.emergencyContactDeleteIcon}>
                            <path fill="currentColor" d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z" />
                          </svg>
                        </button>
                      </span>
                    </td>
                  </tr>
                  {(type.contacts || []).map((contact) => (
                    <tr key={contact.id || contact.contact_name}>
                      <td className={`${styles.likelihoodBodyLabel} ${styles.emergencyContactsNameCell}`}>
                        <div className={styles.emergencyContactCellContent}>
                          <span>{contact.contact_name || "-"}</span>
                        </div>
                      </td>
                      <td className={`${styles.likelihoodBodyValue} ${styles.emergencyContactsPhoneCell}`}>
                        {contact.phone_number || "-"}
                      </td>
                      <td className={`${styles.likelihoodBodyLabel} ${styles.emergencyContactsNameCell}`}>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          {contact ? (
                            <span className={styles.emergencyContactActions}>
                              <button
                                type="button"
                                disabled={loading}
                                className={styles.emergencyContactActionButton}
                                onClick={() => onOpenEdit(type.id, contact.id)}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className={styles.emergencyContactEditIcon}>
                                  <path fill="currentColor" d="M362.7 19.3L314.3 67.7 444.3 197.7l48.4-48.4c25-25 25-65.5 0-90.5L453.3 19.3c-25-25-65.5-25-90.5 0zm-71 71L58.6 323.5c-10.4 10.4-18 23.3-22.2 37.4L1 481.2C-1.5 489.7 .8 498.8 7 505s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L421.7 220.3 291.7 90.3z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                disabled={loading}
                                className={styles.emergencyContactActionButton}
                                onClick={() => onDelete(type.id, contact.id)}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className={styles.emergencyContactDeleteIcon}>
                                  <path fill="currentColor" d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z" />
                                </svg>
                              </button>
                            </span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="row mt-2">
        <div className="col-12 d-flex justify-content-end">
          <button type="button" className={jobStyles.PrimaryButton} disabled={loading} onClick={onOpenAdd}>
            Add Emergency Contact
          </button>
        </div>
      </div>

      {showModal ? (
        <EmergencyContactAddEditModal
          visible={showModal}
          mode={editContext ? "Edit" : "Add"}
          initialValues={editContact ? {
            emergency_contact_type: {
              label: editType && editType.emergency_type ? editType.emergency_type : "",
              value: editType && editType.id ? editType.id : ""
            },
            contact_name: editContact.contact_name || "",
            contact_number: editContact.phone_number || ""
          } : null}
          lockType={!!editContext}
          onClose={onCloseModal}
          typeOptions={typeOptions}
        />
      ) : null}

      {showTypeEditModal ? (
        <EmergencyContactTypeEditModal
          visible={showTypeEditModal}
          initialValues={typeEditContext ? {
            emergency_type: typeEditContext && typeEditContext.emergency_type ? typeEditContext.emergency_type : ""
          } : null}
          onClose={onTypeModalClose}
        />
      ) : null}
    </div>
  );
}
